/**
 * @file data manipulation for calendar
 * @author tobias.frei@notch-interactive.com
 *
 * @module components/calendar/filter-data
 */

import model from './filter-model';

const instance = {};


/**
 * initial fetch data from ajax
 *
 * @param {object} requestURL - API endpoint to get the calendar data
 * @return {void}
 */
instance.fetchAjaxData = (requestURL) => {
    console.time('AJAX call');

    // Clear current list
    model.calendar.list = [];

    let request = new XMLHttpRequest();
    request.open('GET', requestURL, true);

    request.onload = function () {
        console.timeEnd('AJAX call');
        if (this.status >= 200 && this.status < 400) {
            let data = JSON.parse(this.response);

            // Reset actual query amount on new query
            model.calendar.actQueryAmount = model.calendar.queryAmount;

            // Process data
            model.calendar.fetchQueryData(data);

        } else {
            throw 'no ajax response';
        }
    };

    request.onerror = function (error) {
        throw error;
    };

    request.send();
};


/**
 * fetch query data
 *
 * @param {object} data - query data
 * @return {void}
 */
instance.fetchQueryData  = (data) => {
    let itemsLeft,
        payload,
        coverRet,
        coverAmount;

    // Set ui ready flag to remove loader
    model.calendar.ready = true;

    // Reset query offset
    model.calendar.actQueryOffset = 0;

    // Store query data
    model.calendar.querydata = data;

    // Calc items left and get payload
    itemsLeft = data.length - model.calendar.queryAmount;
    if (itemsLeft < 0) itemsLeft = 0;
    payload = data.slice(0, model.calendar.queryAmount);

    // Inject covers and get amount
    coverRet = instance.injectCovers(payload);

    // Cover amount
    coverAmount = coverRet.coverAmount;

    // Fix rows if covers injected
    if (coverAmount > 0) {
        let scanIndex = parseInt(payload.length - coverAmount);

        // Remove last items in half row
        for (let i = payload.length - 1; i >= scanIndex; i--) {
            if (payload[i].cov) {
                coverAmount--;
            }
        }

        // Get items left
        itemsLeft = data.length - (model.calendar.queryAmount - parseInt(coverAmount));
        if (itemsLeft < 0) itemsLeft = 0;

        // Update payload
        payload = data.slice(0, (model.calendar.queryAmount - parseInt(coverAmount)));

        // Try to inject covers again after first covers are shifted (blackhole)
        coverRet = instance.injectCovers(payload);

        // Store amount and hook offset
        model.calendar.actQueryAmount = model.calendar.actQueryAmount - parseInt(coverAmount);
        model.calendar.actQueryOffset -= parseInt(coverAmount);
    }

    // Update payload after cover injection
    payload = coverRet.data;

    // Store payload
    model.calendar.list = payload;

    // Update more amount
    model.calendar.moreAmount = itemsLeft;

    // Set more flags
    model.calendar.hasmore = (itemsLeft > 0);

    // Set tiles after frame update
    window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
            model.calendar.setTileStyles();
        });
    });

};


/**
 * fetch query data from storage
 *
 * @param {number} moreAmount - amount of items to add from storage.
 * @return {void}
 */
instance.fetchStorageData = (moreAmount) => {
    let data = model.calendar.querydata,
        itemsLeft,
        payload,
        coverRet,
        coverAmount;

    // Init loader
    model.calendar.loadernext = false;

    // Update loader after frame update
    window.requestAnimationFrame(function () {
        model.calendar.loadernext = true;
    });

    // Shift query amount
    model.calendar.actQueryAmount += moreAmount;

    // Slice new payload
    payload = data.slice(0, model.calendar.actQueryAmount);

    // Inject covers
    coverRet = instance.injectCovers(payload);

    // Get cover amount
    coverAmount = coverRet.coverAmount + model.calendar.actQueryOffset;

    // Fix rows if covers injected
    if (coverAmount !== 0) {
        let scanIndex = parseInt(payload.length - coverAmount);

        for (let i = payload.length - 1; i >= scanIndex; i--) {
            if (payload[i].cov) {
                coverAmount--;
            }
        }

        // Fillup row or remove last items
        model.calendar.actQueryAmount -= parseInt(coverAmount);

        // Update payload
        payload = data.slice(0, model.calendar.actQueryAmount);

        // Try to inject covers again after first covers are shifted (blackhole)
        coverRet = instance.injectCovers(payload);

        // Update query offset
        model.calendar.actQueryOffset -= coverAmount;
    }

    // Update payload after cover injection
    payload = coverRet.data;

    // Store payload
    model.calendar.list = payload;

    // Get items left and store them
    itemsLeft = data.length - model.calendar.actQueryAmount;
    model.calendar.moreAmount = itemsLeft;

    // Check for ui flags
    if (itemsLeft < 1) model.calendar.hasmore = false;

    // set tile styles after frame updates
    window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
            model.calendar.setTileStyles();
        });
    });
};


/**
 * inject covers on payload
 *
 * @param {object} data - event data array
 * @return {object} - event data array with covers and cover amount
 */
instance.injectCovers = (data) => {
    console.time('cover injection');

    let lastCovName = '',
        lastCovIndex = -1,
        obj = {},
        covName,
        indexOffset = 0,
        insertObj = {},
        coverAmount = 0;

    data.forEach((dateObj, index) => {
        let ts = dateObj.t1 * 1000,
            timestring = new Date(ts).toISOString().split('T')[0],
            split = timestring.split('-'),
            year = split[0],
            month = split[1];

        covName = window.DS.translations.months[month - 1] + ' ' + year;

        // New cover start
        if (covName !== lastCovName) {

            function guid() {
                function s4() {
                    return Math.floor((1 + Math.random()) * 0x10000)
                        .toString(16)
                        .substring(1);
                }

                return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
                    s4() + '-' + s4() + s4() + s4();
            }

            // Add last month cover if exist and new month is start
            if (Object.keys(obj).length > 0 && obj.constructor === Object) {
                insertObj[lastCovIndex] = obj;
                coverAmount++;
            }

            // Store cover index
            lastCovIndex = index;

            // Create new cover
            obj = {};
            obj.id = guid(); // Every cover need always a new id (otherwise duplicate error on previous query)
            obj.t1 = dateObj.t1 - 1;
            obj.t2 = dateObj.t2 - 1;
            obj.coverdate = covName;
            obj.covername = 'Events';
            obj.cov = true;
        }

        lastCovName = covName;
    });

    // Add last month cover if exist
    if (covName === lastCovName && Object.keys(obj).length > 0 && obj.constructor === Object && lastCovIndex !== -1) {
        insertObj[lastCovIndex] = obj;
        coverAmount++;
    }

    // Insert covers with index offset
    for (let key in insertObj) {
        if (insertObj.hasOwnProperty(key)) {
            data.splice(parseInt(key) + parseInt(indexOffset), 0, insertObj[key]);
            indexOffset++;
        }
    }

    // Attach index to items for sorting reasons
    for (let i = 0; i < data.length; i++) {
        data[i].index = i;
    }

    console.timeEnd('cover injection');

    return {
        data: data,
        coverAmount: coverAmount
    };
};


export default instance;
